import {useHierarchy} from '@domain/kpis'
import {useUpmPlantsContext} from '@hconnect/upm'
import {PlantHierarchyType, usePlantHierarchyQuery} from '@hproduce/suite-core'
import {useMemo} from 'react'

import {
  findSuiteHierarchyNode,
  KpiLocationResolution,
  resolveSuiteNodeKpiLocation
} from '../utils/hierarchy-mapping.utils'

type SuiteKpiLocation = {isLoading: boolean; resolution?: KpiLocationResolution}

/** Maps the selected Suite (UPM) hierarchy node to the matching KPI dashboard location */
export const useSuiteKpiLocation = (hierarchyId: string | null): SuiteKpiLocation => {
  const {data: suiteHierarchy, isLoading: isSuiteHierarchyLoading} = usePlantHierarchyQuery()
  const {data: kpiHierarchy, isLoading: isKpiHierarchyLoading} = useHierarchy()
  const plantsMap = useUpmPlantsContext({isInitialLoad: true})

  return useMemo<SuiteKpiLocation>(() => {
    const suiteNode =
      suiteHierarchy && hierarchyId
        ? findSuiteHierarchyNode(suiteHierarchy, hierarchyId)
        : undefined

    if (suiteNode?.type === PlantHierarchyType.Global) {
      return {isLoading: false, resolution: {type: 'global'}}
    }
    if (isSuiteHierarchyLoading || isKpiHierarchyLoading || !plantsMap) {
      return {isLoading: true}
    }
    if (!suiteNode || !kpiHierarchy) {
      return {isLoading: false, resolution: {type: 'notFound'}}
    }

    const plants = Object.values(plantsMap)
    const getPlantId = (upmId: string) => plants.find((plant) => plant.upmId === upmId)?.plantId

    return {
      isLoading: false,
      resolution: resolveSuiteNodeKpiLocation(kpiHierarchy, suiteNode, getPlantId)
    }
  }, [
    hierarchyId,
    suiteHierarchy,
    kpiHierarchy,
    plantsMap,
    isSuiteHierarchyLoading,
    isKpiHierarchyLoading
  ])
}
